import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import type { GrnRegisterRow } from '@/types/reports';
import { withId } from '@/pages/reports/reportHelpers';
import { isWithinDateRange } from '@/lib/dateRangeFilter';
import PortalDateRangeFilter from './PortalDateRangeFilter';

export default function ClientPortalGrnPage() {
  const { t } = useTranslation('clientPortal');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: rawRows = [], isLoading } = useQuery({
    queryKey: ['client-portal', 'grn-register'],
    queryFn: async () => (await apiClient.get<GrnRegisterRow[]>('/client-portal/grn-register')).data,
  });
  const filteredRows = useMemo(
    () => rawRows.filter((r) => isWithinDateRange(r.grnDate, fromDate, toDate)),
    [rawRows, fromDate, toDate],
  );
  const rows = withId(filteredRows, (r) => `${r.grnId}-${r.materialCode}`);

  const columns: DataTableColumn<GrnRegisterRow>[] = [
    { field: 'grnNumber', headerName: t('grnPage.table.grnNumber') },
    { field: 'grnDate', headerName: t('grnPage.table.grnDate'), render: (r) => new Date(r.grnDate).toLocaleDateString() },
    { field: 'supplierName', headerName: t('grnPage.table.supplierName'), filterable: true },
    { field: 'warehouseName', headerName: t('grnPage.table.warehouseName'), filterable: true },
    { field: 'materialName', headerName: t('grnPage.table.materialName'), filterable: true },
    { field: 'acceptedQty', headerName: t('grnPage.table.acceptedQty'), align: 'right' },
    { field: 'status', headerName: t('grnPage.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
  ];

  return (
    <>
      <PageHeader title={t('grnPage.title')} subtitle={t('grnPage.subtitle')} />
      <PortalDateRangeFilter fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} />
      <DataTable columns={columns} rows={rows} loading={isLoading} searchable exportFileName="grn-register" />
    </>
  );
}
